import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { manageUsersPageQueryOptions } from "~/app/api/pageData/manageUsersPageQuery";
import type { ManageUsersPageInitialData, ManageUsersSearch } from "~/app/api/pageData/manageUsersPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import {
  createManageUsersPage,
  type UserAccountsJSPProps,
} from "~/pages/ManageUserAccountsPage/ManageUserAccountsPage";

const MANAGE_USERS_PATH = "/my-company/organization-management/manage-users";

function ManageUsersRoute() {
  const result = useLoaderData({
    from: MANAGE_USERS_PATH,
  }) as PageInitialData<ManageUsersPageInitialData>;
  const shellProps = useStorefrontShellProps();
  const pageData = parsePageData(result.data?.pageDataJson);

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {pageData ? createManageUsersPage(pageData) : null}
    </StorefrontShellFrame>
  );
}

function parsePageData(pageDataJson: string | undefined): UserAccountsJSPProps | null {
  if (!pageDataJson) {
    return null;
  }
  try {
    return JSON.parse(pageDataJson) as UserAccountsJSPProps;
  } catch {
    return null;
  }
}

export async function loadManageUsersPageData(context: { queryClient: QueryClient }, search: ManageUsersSearch) {
  const result = await context.queryClient.ensureQueryData(manageUsersPageQueryOptions(search));

  return unwrapPageInitialData(result);
}

export const manageUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: MANAGE_USERS_PATH,
  validateSearch: (search: Record<string, unknown>): ManageUsersSearch => ({
    page: Number(search.page ?? 0),
    show: String(search.show ?? "All"),
    sort: String(search.sort ?? "name"),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => loadManageUsersPageData(context, deps),
  component: ManageUsersRoute,
});
